import { type JanusCtx, type Row, handBack, record, scalar } from './context.js';
import { computedValue, itemsCol, layerEntriesOf } from '../../shared/array-types.js';
import { FormulaDivisionByZeroError, readCasterGoldTotalInteger, replaceFormulaClassLevels, resolveFormulaArithmeticValue } from '../compile/formula-variable-resolution.js';
import { isPhpNumeric, phpJsonFloat } from '../compile/php-decimal-values.js';
import { phpJsonInteger } from '../compile/php-integer-arithmetic.js';
import { phpJsonString } from '../compile/php-json-values.js';
import contract from '../compile/c1-contract.json';

const LAYER_TAGS = new Set(['body', 'mind', 'soul', 'spirit', 'luck', 'fortune', 'total_level',
  'speed_walking', 'speed_climbing', 'speed_swimming', 'speed_hovering', 'speed_flying', 'speed_burrowing', 'v_lost']);
const ALIASES = new Map([['walking_speed', 'speed_walking'], ['swimming_speed', 'speed_swimming']]);
const CLASS_TAGS = new Set(contract.rollup.classes.map(entry => `${entry.variable_name}_level`));
type FormulaContext = { read: Pick<JanusCtx['read'], 'value'> & Partial<Pick<JanusCtx['read'], 'entries'>> };

/** discern_variable_tags on the action's actual holder. Missing inputs stop the
 * browser action by name; arithmetic/class/gold semantics share the compiler twin.
 * integer retains PHP's final (int) coercion before projecting to a JS number.
 */
export function discernVariableTags(ctx: FormulaContext, raw: unknown, holder: Row,
  options: { x?: unknown; reason: string; integer?: boolean }): number {
  const missing = (tag: string): never => handBack(`${options.reason}:${tag}-not-held`);
  const held = (value: unknown, tag: string): string | number => {
    if (!isPhpNumeric(value) || !Number.isFinite(Number(value))) missing(tag);
    return value as string | number;
  };
  // A compiled vitality bucket elides a zero loss. An absent bucket remains unreadable.
  const lostVitality = (): unknown => computedValue(holder, 'vitality', 'v_lost')
    ?? (computedValue(holder, 'vitality', 'effective_v_max') != null ? 0 : undefined);
  if (typeof raw !== 'string' && typeof raw !== 'number') handBack(`${options.reason}:invalid-formula`);
  let expression = phpJsonString(raw);
  if (expression === 'v_lost') expression = phpJsonString(held(lostVitality(), 'v_lost'));
  expression = expression.replace(/\[([^\]]*)\]/g, (tag, name: string) => {
    if (name === 'x') return String(phpJsonInteger(held(options.x, name)));
    const alias = ALIASES.get(name);
    if (name === 'luck' || name === 'total_level' || name === 'v_lost')
      return phpJsonString(held(itemsCol(holder, name) ?? ctx.read.value(holder, name)
        ?? (name === 'v_lost' ? lostVitality() : undefined), name));
    if (LAYER_TAGS.has(name) || alias !== undefined)
      return phpJsonString(held(ctx.read.value(holder, alias ?? name), name));
    if (name === 'spellcasting_essence') {
      let modifier = ctx.read.value(holder, 'spellcasting_essence_mod');
      if (modifier == null) {
        const essence = scalar(ctx.read.value(holder, 'spellcasting_essence'));
        const entry = Object.entries(contract.rollup.essences).find(([, id]) => id === essence);
        if (entry) modifier = itemsCol(holder, entry[0]);
      }
      return String(phpJsonInteger(held(modifier, name)));
    }
    if (name === 'v_max' || name === 'half_v_max') {
      const maximum = phpJsonInteger(held(computedValue(holder, 'vitality', 'effective_v_max'), name));
      return String(name === 'half_v_max' ? maximum / 2n : maximum);
    }
    if (name === 'gold_total') {
      // The page collection reader restores lean omitted empty inventories.
      const root = holder.inventory;
      const entries = ctx.read.entries ?? layerEntriesOf;
      const rows = root ?? entries(holder, 'inventory');
      if (!Array.isArray(rows) && !record(rows)) missing(name);
      return String(readCasterGoldTotalInteger(holder, {
        layerValue: (row, key) => ctx.read.value(record(row), key),
        layerEntries: row => entries(record(row), 'inventory'),
        itemField: itemsCol, computedValue,
      }, () => missing(name)));
    }
    // The shared class helper preserves catalog order and PHP's first-class-only replacement.
    if (CLASS_TAGS.has(name)) held(replaceFormulaClassLevels(holder, tag, contract.rollup), name);
    return tag;
  });
  expression = replaceFormulaClassLevels(holder, expression, contract.rollup);
  const unresolved = expression.match(/\[([^\]]*)\]/);
  if (unresolved) missing(unresolved[1] || 'unknown-tag');
  if (/[{}]/.test(expression) || expression.includes('[') || expression.includes(']')) handBack(`${options.reason}:invalid-formula`);
  let value: unknown;
  try { value = resolveFormulaArithmeticValue(expression); }
  catch (error) {
    if (error instanceof FormulaDivisionByZeroError) handBack(`${options.reason}:division-by-zero`);
    handBack(`${options.reason}:invalid-formula`);
  }
  const result = typeof value === 'bigint' ? Number(value)
    : options.integer ? Number(phpJsonInteger(value)) : phpJsonFloat(value);
  if (!Number.isFinite(result)) handBack(`${options.reason}:invalid-formula`);
  return result;
}
